import React, { useState, useEffect, useRef } from "react";
import { Slider } from "@/shared/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { AspectRatio } from "@/shared/ui/aspect-ratio";
import { LutRenderer } from "./LutRenderer";

interface GalleryItem {
  id: string;
  before: string;
  after?: string;
  name?: string;
}

interface LutPreviewDialogProps {
  isOpen: boolean;
  onClose: () => void;
  galleryItems: GalleryItem[];
  lutFilename: string | null;
}

export const LutPreviewDialog = ({
  isOpen,
  onClose,
  galleryItems = [],
  lutFilename,
}: LutPreviewDialogProps) => {
  const [intensity, setIntensity] = useState(80);
  const [currentIndex, setCurrentIndex] = useState(0);
  const imgRef = useRef<HTMLImageElement>(null);
  const [imageRect, setImageRect] = useState({ width: 0, height: 0 });
  const [naturalAspectRatio, setNaturalAspectRatio] = useState(16 / 9);
  
  const currentItem = galleryItems[currentIndex];
  const hasMultipleItems = galleryItems.length > 1;

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % galleryItems.length);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + galleryItems.length) % galleryItems.length);
  };

  // Reset index when dialog is opened/closed
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0);
    }
  }, [isOpen]);

  // Update aspect ratio when current item changes
  useEffect(() => {
    if (!currentItem?.before || !isOpen) return;

    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      setNaturalAspectRatio(img.width / img.height);
    };

    img.src = currentItem.before;
  }, [currentItem?.before, isOpen]);

  // Depois que a imagem renderiza visualmente, captura seu tamanho real renderizado
  useEffect(() => {
    if (!isOpen || !imgRef.current) return;

    const updateRect = () => {
      const rect = imgRef.current?.getBoundingClientRect();
      if (rect?.width && rect?.height) {
        setImageRect({ width: rect.width, height: rect.height });
      }
    };

    const raf = requestAnimationFrame(updateRect);
    window.addEventListener("resize", updateRect);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", updateRect);
    };
  }, [isOpen, naturalAspectRatio]);

  // Check if we can render the LUT
  const canRenderLut = Boolean(
    currentItem?.before &&
    lutFilename &&
    imageRect.width > 0 &&
    imageRect.height > 0
  );

  if (!currentItem) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px] bg-[#1A1A1A] border-white/10 text-white max-h-screen">
        <DialogHeader>
          <DialogTitle className="text-white">Teste de LUT</DialogTitle>
          <DialogDescription className="text-white/70">
            Visualize como seu LUT será aplicado nas imagens
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-6">
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <p className="text-sm text-white/80">
                {currentItem.name || 'Pré-visualização do LUT'}
              </p>
              {hasMultipleItems && (
                <div className="text-sm text-white/60">
                  {currentIndex + 1} / {galleryItems.length}
                </div>
              )}
            </div>

            <div className="relative">
              <div
                className="bg-black/40 rounded-md overflow-hidden w-full flex items-center justify-center relative"
                style={{ minHeight: 200, maxHeight: "60vh" }}
              >
                {/* Navigation Buttons */}
                {hasMultipleItems && galleryItems.length > 1 && (
                  <>
                    <button
                      onClick={goToPrevious}
                      className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 rounded-full p-2 transition-colors"
                      aria-label="Imagem anterior"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m15 18-6-6 6-6" />
                      </svg>
                    </button>
                    <button
                      onClick={goToNext}
                      className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 rounded-full p-2 transition-colors"
                      aria-label="Próxima imagem"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    </button>
                  </>
                )}

                <AspectRatio
                  ratio={naturalAspectRatio}
                  className="w-full h-full flex items-center justify-center"
                >
                  {currentItem.before ? (
                    <div className="relative w-full h-full">
                      {/* Before/After Comparison */}
                      <div className="absolute inset-0">
                        <img
                          ref={imgRef}
                          src={currentItem.before}
                          alt="Antes"
                          className="w-full h-full object-contain"
                          crossOrigin="anonymous"
                        />
                      </div>
                      
                      {/* After Image with LUT - Full width with opacity based on intensity */}
                      {lutFilename && canRenderLut && (
                        <div className="absolute inset-0">
                          <LutRenderer
                            width={imageRect.width}
                            height={imageRect.height}
                            imageUrl={currentItem.before}
                            lutUrl={lutFilename}
                            intensity={intensity / 100}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center text-white/50">
                      Nenhuma imagem disponível
                    </div>
                  )}
                </AspectRatio>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/70">Intensidade do LUT</span>
              <span className="text-sm font-mono bg-white/10 px-2 py-1 rounded">
                {intensity}%
              </span>
            </div>
            <div className="relative">
              <Slider
                value={[intensity]}
                onValueChange={([value]) => setIntensity(value)}
                min={0}
                max={100}
                step={1}
                className="relative w-full"
                trackClassName="bg-white/20 h-1"
                rangeClassName="bg-white/80"
                thumbClassName="bg-white border-2 border-white/90 shadow-md"
              />
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              className="border-white/20 text-black bg-white hover:bg-white/80 hover:text-black"
            >
              Fechar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
